import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  } from "@ant-design/icons";
import {
  Button,
  Drawer,
  Spin,
  Typography,
  Table,
  Card,
  Space,
  Input,
  message,
  Badge,
  Tooltip,
  Empty,
  Form,
  Descriptions,
} from "antd";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  addProductsToPromotionAPI,
  getByIdPointPromotion,
  updateProductCoefficientAPI,
} from "../../../api/pointPromotion/pointPromotion";
import ButtonWithPermission from "../../../components/ButtonWithPermission/ButtonWithPermission";
import EditableCellCoefficient from "./EditableCellCoefficient";
import ServiceProductSelector from "../../../components/common/ServiceProductSelector";
import { errorMessage } from "../../../lib/apiError";
import { neutral, brand, spacing, radius, border } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;
const { Search } = Input;

export default function ViewPointPromotion({
  isModalOpen,
  handleCancel,
  record,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Modal chọn sản phẩm để thêm vào chương trình (dùng ServiceProductSelector)
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);


  const fetchPromotionDetail = async (promotionId) => {
    try {
      setIsLoading(true);
      const response = await getByIdPointPromotion({ id: promotionId });
      setDetailData(response?.data ?? null);
    } catch (error) {
      message.error(errorMessage(error, "Lỗi khi tải chi tiết chương trình"));
    } finally {
      setIsLoading(false);
    }
  };
  // Effect chính để fetch data
  useEffect(() => {
    if (isModalOpen && record?.id) {
      fetchPromotionDetail(record.id);
    }
  }, [isModalOpen, record?.id]);

  // Lấy tên đơn vị
  const getBranchName = () => {
    if (!detailData?.mcoupon_branch_id) return "N/A";
    return detailData?.mcoupon_branch ? detailData.mcoupon_branch.name : "N/A";
  };

  // Lấy tên cơ chế
  const getMechanismName = () => {
    if (!detailData?.point_mechanism_id) return "N/A";
    return detailData.point_mechanism ? detailData.point_mechanism.name : "N/A";
  };

  // Dữ liệu sản phẩm từ detailData.items
  const productData = React.useMemo(() => {
    if (!detailData?.items) return [];

    return detailData.items.map((item) => ({
      key: item.id,
      id: item.id,
      name: item.service_product?.name || "N/A",
      code: item.service_product?.code || "N/A",
      price: item.service_product?.price || 0,
      is_active: item.service_product?.is_active || false,
      coefficient: item.coefficient || "_",
    }));
  }, [detailData?.items]);

  // Lọc sản phẩm theo từ khóa tìm kiếm
  useEffect(() => {
    if (!searchText) {
      setFilteredProducts(productData);
    } else {
      const filtered = productData.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.code?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchText, productData]);

  // Id sản phẩm (service_product) đã có trong chương trình — để tránh thêm trùng.
  const currentProductIds = React.useMemo(
    () =>
      (detailData?.items || [])
        .map((it) => it.service_product_id ?? it.service_product?.id)
        .filter(Boolean),
    [detailData?.items]
  );

  // Thêm sản phẩm được chọn từ ServiceProductSelector vào chương trình (chỉ thêm cái mới).
  const handleAddSelected = async (ids = []) => {
    const toAdd = ids.filter((id) => !currentProductIds.includes(id));
    setIsAddProductModalOpen(false);

    if (!record?.id) {
      message.error("Không tìm thấy thông tin chương trình");
      return;
    }
    if (toAdd.length === 0) {
      message.info("Các sản phẩm đã có trong chương trình");
      return;
    }

    try {
      setIsLoading(true);
      await addProductsToPromotionAPI({
        point_promotion_id: record.id,
        products: toAdd,
      });
      message.success(`Đã thêm ${toAdd.length} sản phẩm vào chương trình`);
      await fetchPromotionDetail(record.id);
    } catch (error) {
      message.error(errorMessage(error, "Lỗi khi thêm sản phẩm"));
    } finally {
      setIsLoading(false);
    }
  };

  // Hàm đóng modal chi tiết và reset states
  const handleModalCancel = () => {
    setDetailData(null);
    setSearchText("");
    setFilteredProducts([]);
    setIsAddProductModalOpen(false);
    handleCancel();
  };

  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;
  const edit = (record) => {
    form.setFieldsValue({
      coefficient: record?.coefficient,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (id) => {
    try {
      const row = await form.validateFields();
      setIsLoading(true);

      await updateProductCoefficientAPI({
        id,
        data: { coefficient: row.coefficient },
      });
      message.success("Cập nhật hệ số thành công!");
      setEditingKey("");
      await fetchPromotionDetail(record.id);
    } catch (error) {
      // `validateFields` ném lỗi có `errorFields` — antd đã hiện lỗi tại ô, đừng
      // chồng thêm toast.
      if (error?.errorFields) return;
      message.error(errorMessage(error, "Có lỗi xảy ra trong quá trình cập nhập!"));
    } finally {
      setIsLoading(false);
    }
  };

  // Tag trạng thái / loại — gọn, không emoji, dùng màu antd chuẩn.
  const renderStatusTag = (status) =>
    status ? <StatusTag tone="success" dot>Hoạt động</StatusTag> : <StatusTag tone="danger" dot>Không hoạt động</StatusTag>;

  const renderTypeTag = (type) =>
    type === "PRODUCT" ? <StatusTag>Sản phẩm</StatusTag> : <StatusTag>Khác</StatusTag>;

  /* Ẩn "Kiểu áp dụng" (promotion_type) — tính năng chưa dùng.
  const renderPromotionTypeTag = (promotionType) =>
    promotionType === "CHOOSABLE" ? (
      <StatusTag>Tùy chọn</StatusTag>
    ) : (
      <StatusTag>Mặc định</StatusTag>
    );
  */

  // ===================== CẢI THIỆN GIAO DIỆN - CẤU HÌNH CỘT BẢNG VỚI STYLE TỐT HỢN =====================
  const productColumns = [
    {
      title: "STT",
      key: "index",
      width: 70,
      fixed: "left",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Tên sản phẩm",
      dataIndex: "name",
      key: "name",
      width: 320,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text style={{ fontWeight: 500 }}>{text || "—"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Mã sản phẩm",
      dataIndex: "code",
      key: "code",
      width: 200,
      render: (text) => <StatusTag>{text || "—"}</StatusTag>,
    },
    {
      title: "Hệ số",
      dataIndex: "coefficient",
      key: "coefficient",
      align: "center",
      width: 120,
      editable: true,
      render: (text, record) => {
        const editable = isEditing(record);
        return editable ? text : <b>{text || "_"}</b>;
      },
    },

    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record);
        return (
          <Space size="middle">
            {editable ? (
              <Space>
                <ButtonWithPermission
                  actions={["point-promotion.updateStatus", "reset.rank"]}
                  children={"Lưu"}
                  size="small"
                  color="green"
                  onClick={() => {
                    save(record.id);
                  }}
                  noPermissionText={"Không có quyền truy cập"}
                />
                <Button onClick={cancel} size="small" type="default" danger>
                  Hủy
                </Button>
              </Space>
            ) : (
              <Space>
                <ButtonWithPermission
                  actions={["point-promotion.updateStatus", "reset.rank"]}
                  type="text"
                  size="small"
                  style={{
                    color: "#2543ec",
                  }}
                  icon={<EditOutlined style={{ fontSize: 16 }} />}
                  onClick={() => edit(record)}
                  noPermissionText={"Không có quyền truy cập"}
                />
              </Space>
            )}
          </Space>
        );
      },
    },
  ];

  const mergedColumns = productColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <Drawer
        title={
          <Space style={{ fontSize: 16, fontWeight: 600, color: neutral[900] }}>
            <EyeOutlined style={{ color: brand.primary }} />
            <span>Chi tiết chương trình tích điểm</span>
          </Space>
        }
        open={isModalOpen}
        onClose={handleModalCancel}
        // Rộng hơn hai drawer chi tiết còn lại vì bên trong có bảng sản phẩm
        // (mã / tên / hệ số / thao tác) và ô sửa hệ số ngay trên dòng.
        width="min(960px, 100vw)"
        // Gỡ bảng sản phẩm khỏi cây khi đóng; state của component do
        // `handleModalCancel` reset.
        destroyOnClose
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={handleModalCancel}>Đóng</Button>
          </Space>
        }
        styles={{
          // Drawer tự cuộn body -> bỏ maxHeight "calc(100vh - 200px)" phải canh tay.
          body: { padding: spacing[5] },
          header: { borderBottom: border.hairline },
        }}
      >
        {isLoading || !detailData ? (
          <div style={{ textAlign: "center", padding: "80px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#666" }}>
              Đang tải thông tin chương trình...
            </div>
          </div>
        ) : (
          <div>
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2 }}
              style={{ marginBottom: spacing[5], background: neutral[0] }}
            >
              <Descriptions.Item label="Tên chương trình" span={2}>
                {detailData?.name || record?.name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Đơn vị">{getBranchName()}</Descriptions.Item>
              <Descriptions.Item label="Loại cơ chế">{getMechanismName()}</Descriptions.Item>
              <Descriptions.Item label="Loại khuyến mãi">{renderTypeTag(detailData?.type)}</Descriptions.Item>
              <Descriptions.Item label="Trạng thái">{renderStatusTag(detailData?.is_active)}</Descriptions.Item>
              <Descriptions.Item label="Ngày bắt đầu">
                {detailData?.start_date ? dayjs(detailData.start_date).format("DD/MM/YYYY HH:mm") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày kết thúc">
                {detailData?.end_date ? dayjs(detailData.end_date).format("DD/MM/YYYY HH:mm") : "—"}
              </Descriptions.Item>
            </Descriptions>

            {/* ===================== CẢI THIỆN GIAO DIỆN - CARD DANH SÁCH SẢN PHẨM ===================== */}
            {detailData?.type === "PRODUCT" && (
              <Card
                size="small"
                title={
                  <Space style={{ fontSize: 14, fontWeight: 600 }}>
                    <span>Danh sách sản phẩm áp dụng</span>
                    <Badge count={productData.length} style={{ backgroundColor: brand.primary }} />
                  </Space>
                }
                style={{ borderRadius: radius.md, border: border.hairline }}
                extra={
                  <Space>
                    <Search
                      placeholder="Tìm sản phẩm..."
                      allowClear
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ width: 240 }}
                      prefix={<SearchOutlined style={{ color: neutral[400] }} />}
                    />
                    {/* <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsAddProductModalOpen(true)}
                      style={{
                        borderRadius: "8px",
                        height: "36px",
                        fontWeight: 500,
                      }}
                    >
                      Thêm sản phẩm
                    </Button> */}

                    <ButtonWithPermission
                      actions={["point-promotion.addProductsToPromotionAPI"]}
                      children={"Thêm sản phẩm"}
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsAddProductModalOpen(true)}
                      style={{
                        borderRadius: "8px",
                        height: "34px",
                        fontWeight: 500,
                      }}
                      noPermissionText={"Không có quyền truy cập"}
                    />
                  </Space>
                }
              >
                {/* THAY ĐỔI: Thêm thông báo khi không có sản phẩm */}
                {filteredProducts.length === 0 && !searchText ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div>
                        <Text style={{ fontSize: "16px", color: "#666" }}>
                          Chưa có sản phẩm nào được áp dụng
                        </Text>
                        <br />
                        <Text style={{ fontSize: "14px", color: "#999" }}>
                          Nhấn "Thêm sản phẩm" để bắt đầu thêm sản phẩm vào
                          chương trình
                        </Text>
                      </div>
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <Form form={form} component={false}>
                    <Table
                      components={{
                        body: {
                          cell: (cellProps) => (
                            <EditableCellCoefficient {...cellProps} />
                          ),
                        },
                      }}
                      columns={mergedColumns}
                      // columns={productColumns}
                      dataSource={filteredProducts}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                          `Hiển thị ${range[0]}-${range[1]} trong tổng số ${total} sản phẩm`,
                        pageSizeOptions: ["10", "20", "50"],
                      }}
                      // scroll={{ x: 800 }}
                      scroll={{ x: "max-content" }}
                      size="middle"
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Không tìm thấy sản phẩm nào phù hợp"
                          />
                        ),
                      }}
                      rowClassName={(record, index) =>
                        index % 2 === 0 ? "table-row-light" : "table-row-dark"
                      }
                      style={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                      }}
                    />
                  </Form>
                )}
              </Card>
            )}
          </div>
        )}

        {/*
          Bộ chọn sản phẩm (là Modal) đặt BÊN TRONG Drawer — có chủ ý.
          Drawer và Modal của antd cùng z-index mặc định 1000; để chúng ngang cấp
          thì thứ tự hiển thị phụ thuộc thứ tự portal được gắn vào body, rất dễ vỡ.
          Lồng vào trong, antd tự nâng z-index cho lớp con nên Modal luôn nổi trên
          Drawer. Modal vẫn portal ra body nên không bị vùng cuộn của Drawer cắt.
        */}
        <ServiceProductSelector
          hideTrigger
          open={isAddProductModalOpen}
          onOpenChange={setIsAddProductModalOpen}
          branchId={detailData?.mcoupon_branch_id}
          value={[]}
          onChange={handleAddSelected}
        />
      </Drawer>
    </>
  );
}
